import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事の認定基準（route=/standards/certification）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /standards/aptitude や /butler/become と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 創立1890年（明治23年）/ 法人設立2025年2月19日 / 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格 / 民間認定であり国家資格ではない
// 創作した年号・数値・実績・合格率・受講料・輩出人数は書かない

export default function StandardsCertificationPage() {
  useEffect(() => {
    updateSEO(seoData['/standards/certification']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '基準・指針', href: '/standards' },
    { label: '執事の認定基準' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const publishReasons = [
    {
      en: 'Shared Language',
      ja: '評価の言葉を揃える',
      text: '何をもって「できる」とするかが人ごとに分かれると、学び手も依頼者も迷います。認定基準を公開し、観察・接遇・段取り・自制といった観点を共通の言葉に落とします。'
    },
    {
      en: 'Fair Path',
      ja: '公正な到達点を示す',
      text: '学歴や年齢で入口を閉ざさず、いまの地点から何を積み上げれば次の段階へ進めるかを明示します。基準は門戸を狭める壁ではなく、歩幅を測る道標です。'
    },
    {
      en: 'Trust Boundary',
      ja: '信頼の境界を守る',
      text: '民間認定である以上、できることとできないことを曖昧にしません。認定が意味する範囲と、意味しない範囲を同時に示すことが、協会と社会のあいだの誠実さです。'
    }
  ];

  const associateAxes = [
    {
      title: '指示の正確な実行',
      text: '依頼の意図を聞き取り、手順を飛ばさず完了まで運ぶ力を見ます。速さより、確認の一手を省かない姿勢が土台になります。'
    },
    {
      title: '基本の所作と身だしなみ',
      text: '立ち居振る舞い、挨拶、視線の置き方など、場を乱さない最小限の型を身体に入れているかを確認します。'
    },
    {
      title: '記録と報告の型',
      text: '聞いた好み・禁忌・時刻を短く残し、必要な相手へ事実だけを渡せるか。記憶への過信を避け、再現できる形へ移す習慣を問います。'
    },
    {
      title: '敬語と自制の初動',
      text: '想定外の変更や厳しい言葉のあとでも、表情と呼吸を整え、次の行動へ私情を渡さない初動があるかを見ます。'
    }
  ];

  const professionalAxes = [
    {
      no: '01',
      title: '優先順位の判断',
      text: '複数の依頼が重なるとき、安全と尊厳、約束の時刻、不可逆な手配の順で何を先に守るかを自分の言葉で説明し、実行へ移せるかを見ます。'
    },
    {
      no: '02',
      title: '先回りと距離感',
      text: '言われる前の不便に気づきつつ、干渉にならない一手だけを置く判断。過剰な世話と必要な手配の境界を見極めます。'
    },
    {
      no: '03',
      title: '報告の粒度',
      text: '不安の吐露ではなく、次の判断に必要な事実を短い文で共有できるか。抱え込みすぎず、委ねるべき事柄を適切なタイミングで渡せます。'
    },
    {
      no: '04',
      title: '現場の再現性',
      text: '担当が変わっても品位が落ちないよう、手順・記録・申し送りを整える力。個人の勘に依存しない型として現れるかを確認します。'
    }
  ];

  const masterAxes = [
    {
      en: 'Atmosphere',
      ja: '家全体の空気',
      text: '一室の整えに留まらず、邸内の動線・季節・来客の流れを見渡し、静けさが続く設計を語り、示範できるかを見ます。'
    },
    {
      en: 'Mentorship',
      ja: '後進への手ほどき',
      text: '自分だけが上手くやる段階を超え、基準を言葉と動作で渡せるか。叱責ではなく、短い確認と振り返りで型を残します。'
    },
    {
      en: 'Design',
      ja: '基準の設計',
      text: '専門領域や家ごとの事情に合わせ、観察・接遇・段取りの芯を崩さず運用を組む力。例外処理を例外のまま放置しない姿勢を問います。'
    }
  ];

  const specialtyRoles = [
    {
      label: '縦の成長を補う',
      text: 'Associate・Professional・Masterという段階の深まりに対し、専門資格は特定領域の横断的な精度を補います。芯となる素養は共通のまま、知識と手順を厚くします。'
    },
    {
      label: '領域の例示的な広がり',
      text: '食、衣裳、行事、同行、情報の扱いなど、家の要請に応じて深める方向は多様です。専門は肩書の収集ではなく、現場の穴を塞ぐための選択です。'
    },
    {
      label: '三段階との関係',
      text: '専門資格だけで段階を飛び越えるものではありません。基礎の型、判断の再現、示範の力があってこそ、専門の知識が奉仕のノイズではなく精度へ変わります。'
    }
  ];

  const practicalLenses = [
    {
      title: '所作',
      text: '歩き方、物の扱い、扉と席の動きなど、音と視線を立てすぎない動作か。美しさの演技ではなく、相手の安心につながる静けさを見ます。'
    },
    {
      title: '接遇',
      text: '取次、案内、言葉の長さ、沈黙の使い方。正しさの誇示ではなく、要件が届き関係が傷つかない両立ができているかを確認します。'
    },
    {
      title: '段取り',
      text: '準備・実行・片付け・申し送りの順序。急ぐ場面ほど確認を残し、取り消せない一手の前で一度止まれるかを見ます。'
    }
  ];

  const continuumItems = [
    {
      en: 'Review',
      ja: '振り返りの習慣',
      text: '認定の直後が完成ではありません。行事の前後や担当の変わり目に、判断と記録を短い言葉で見返し、古くなった手順を閉じます。'
    },
    {
      en: 'Update',
      ja: '知識の細更新',
      text: '暮らしの様式、通信、季節の行事は変わり続けます。教養・語学・作法を自慢のためではなく、相手の世界へ寄り添う語彙として更新します。'
    },
    {
      en: 'Exchange',
      ja: '同行と対話',
      text: '一人の現場感覚に閉じず、基準の言葉を他者と突き合わせます。違いを競うのではなく、品位の下限を共有するための対話です。'
    },
    {
      en: 'Return',
      ja: '座標へ戻る',
      text: '迷ったとき、認定時に問われた観点——観察、自制、接遇、段取り——へ立ち返ります。新しい手法も、座標がぶれなければ現場に定着します。'
    }
  ];

  const limitPoints = [
    {
      title: '就業を保証しない',
      text: '認定は、特定の雇用や契約の成立を約束するものではありません。家ごとの要請、人物の相性、時期の条件は、認定の外側で決まります。'
    },
    {
      title: '技能の永久証明ではない',
      text: '一度の審査で、その後のすべての現場品質が固定されるわけではありません。継続的な研鑽と、その家での信頼の積み重ねが品質を支えます。'
    },
    {
      title: '国家資格ではない',
      text: '当協会の資格は民間認定です。法令上の独占業務や公的免許を意味せず、できる範囲を超えて権威を装うことを戒めます。'
    }
  ];

  const faqItems = [
    {
      q: '認定基準を公開すると、対策だけが先行しませんか？',
      a: '基準は暗記用の解答集ではなく、現場で立ち返る観点の一覧です。実技では所作・接遇・段取りの一連の流れを見るため、言葉だけの対策では品位は続きません。公開の目的は、評価の言葉を社会と共有し、学びの方向を揃えることにあります。'
    },
    {
      q: '初級・中級・上級と専門資格は、どの順で目指せばよいですか？',
      a: '基本はAssociateで型を身につけ、Professionalで判断の再現を確かめ、Masterで示範と設計へ進む縦の流れです。専門資格は、その過程で現場に必要な領域を厚くするための補完です。ご自身の実務と生活の条件に合わせ、なり方の案内と資格制度を参照しながら順序を組んでください。'
    },
    {
      q: '認定を受ければ、すぐに就業や独立が保証されますか？',
      a: 'いいえ。認定は就業・契約・収入を保証するものではなく、技能の永久証明でもありません。民間認定として、その時点で当協会が定める観点を満たしたことを示すものであり、その後の研鑽と、個々の現場での信頼形成が品質を決めます。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事の認定基準｜全日本執事協会が定める資格認定の観点',
    description:
      '全日本執事協会が定める執事の認定基準。公開する理由、初級Associate・中級Professional・上級Masterで問われること、専門資格の位置づけ、実技審査の観点、継続研鑽、認定の限界。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards/certification'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '基準・指針', item: 'https://japanbutlerassociation.com/standards' },
      { '@type': 'ListItem', position: 3, name: '執事の認定基準', item: 'https://japanbutlerassociation.com/standards/certification' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-std-certification-01.jpg"
          alt="認定の品位を体現する執事の静かな立ち居振る舞い"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(100deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.7)_50%,rgba(16,20,26,0.32)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Certification Standards
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事の認定基準</span>
              <span className="block text-[#d7b779]">資格認定にあたって見る観点</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創立以来、日本における執事文化の継承に取り組んできました。代表理事・石井裕一（三代目頭取）のもと、本頁は認定団体である当協会が、資格認定にあたって何を見るかを示す基準です。初級Associate・中級Professional・上級Masterの三段階と専門資格、実技の観点、認定後の研鑽、そして認定の限界までを、同じ座標のうえで開きます。当協会の資格は民間認定であり、国家資格ではありません。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 公開理由 — 上リード＋三枚横カード＋下に画像右配置（aptitudeの左縦帯型とは別） */}
          <section>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
              <div className="max-w-2xl">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                  I. Why Publish Standards
                </span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em]">
                  認定基準を<span className="text-[#a38e65]">公開する理由</span>
                </h2>
              </div>
              <p className="font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.14em] shrink-0">
                Clarity · Fairness · Boundary
              </p>
            </div>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10 max-w-3xl">
              認定は、密室の印象評価であってはなりません。何を満たせば次の段階へ進めるのか、何を満たしても約束しないのか——その両面を社会に開くことが、民間認定に携わる協会の責務です。基準の公開は、受験技術の漏洩ではなく、奉仕の品位を共有財産にするための手続きです。
            </p>
            <div className="grid md:grid-cols-3 gap-0 border border-[#1a2942] mb-10">
              {publishReasons.map((item, idx) => (
                <div
                  key={item.en}
                  className={`p-7 sm:p-8 bg-white ${
                    idx < publishReasons.length - 1 ? 'border-b md:border-b-0 md:border-r border-[#e2ddd2]' : ''
                  }`}
                >
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl block mb-3">0{idx + 1}</span>
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">{item.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{item.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[280px_1fr] gap-8 items-center">
              <img
                src="/images/generated/jba-std-certification-02.jpg"
                alt="認定の観点を共有する協会内の打ち合わせ"
                className="w-full h-52 object-cover border border-[#a38e65]/30"
              />
              <div className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <p>
                  素養の座標は
                  <a href="/standards/aptitude" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">執事に必要な素養</a>
                  に、制度の枠組みは
                  <a href="/qualifications" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">資格制度</a>
                  に詳述しています。本頁は「認定の場で何を見るか」に焦点を当て、評価の言葉を現場の手つきへ接続します。
                </p>
                <p>
                  基準・指針の全体は
                  <a href="/standards" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">基準・指針</a>
                  から俯瞰できます。認定基準は単独の規則集ではなく、素養・倫理・実務の指針と読み合わせて初めて機能します。
                </p>
              </div>
            </div>
          </section>

          {/* S2. Associate — 紺サイドレール＋2×2（ヒーロー直下三本柱とは別） */}
          <section>
            <div className="grid lg:grid-cols-[8px_1fr] gap-0 mb-8">
              <div className="hidden lg:block bg-[#1a2942]"></div>
              <div className="bg-[#fbfaf7] border border-[#e2ddd2] border-l-0 lg:border-l-0 px-6 sm:px-10 py-8 sm:py-10">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 mb-4">
                  <span className="font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56]">
                    II. Associate Butler
                  </span>
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em]">初級</span>
                </div>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  初級 Associate Butler で問われること
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                  Associateは、奉仕の「型」を身体に入れる段階です。華やかな裁量より、指示の意図を正確に実行し、基本の所作・記録・敬語・自制を崩さないことを重視します。才能の有無を競う入口ではなく、誰もが反復で整えられる土台の確認です。
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              {associateAxes.map((item) => (
                <div
                  key={item.title}
                  className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] p-7"
                >
                  <div className="w-8 h-px bg-[#a38e65] mb-4"></div>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{item.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[1fr_300px] gap-8 items-center">
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                初級で求めるのは、完成された名手の演出ではありません。確認を省かない、記録を残す、言葉を選び直す——短い手つきを、緊張下でも再現できるかです。学びの始め方は
                <a href="/butler/become" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">執事になるには</a>
                でも案内しています。学歴や年齢による制限は設けておらず、いまの地点から型を積み上げる道を開いています。
              </p>
              <img
              src="/images/gen2/std2-certification-basic-training.jpg"
                alt="初級で求める応対の型を講師と確認する基礎研修の場面"
                className="w-full h-48 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S3. Professional — 上画像フル幅＋番号付き縦リスト */}
          <section>
            <div className="mb-8">
              <img
              src="/images/gen2/std2-certification-priority-judgment.jpg"
              alt="複数の依頼内容を見比べ優先順位を判断する中級執事"
                className="w-full h-56 sm:h-64 object-cover border border-[#a38e65]/30"
              />
            </div>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. Professional Butler
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              中級 Professional Butler で問われること
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              Professionalは、型を現場の再現性へ昇華する段階です。単一の指示を正確にこなすだけでなく、依頼が重なり、予定が揺れ、関係者が増えるなかで、優先順位と先回りと報告の粒度を自分で調整できるかを見ます。
            </p>
            <div className="space-y-0 border-t border-[#1a2942] mb-8">
              {professionalAxes.map((item, idx) => (
                <div
                  key={item.no}
                  className={`grid sm:grid-cols-[72px_1fr] gap-4 sm:gap-6 py-6 ${
                    idx < professionalAxes.length - 1 ? 'border-b border-[#e2ddd2]' : ''
                  }`}
                >
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl">{item.no}</span>
                  <div>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.title}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm shrink-0">
                JUDGE · ANTICIPATE · REPORT
              </span>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#e7e2d8] leading-[1.9]">
                中級の良さは、忙しそうに動くことではありません。次に何が起きるかを短く予告し、関係者が安心できる余白を残す判断に現れます。
              </p>
            </div>
          </section>

          {/* S4. Master — 三列カード＋下リード（Associateの2×2とは別） */}
          <section>
            <div className="bg-[#1a2942] px-6 sm:px-10 py-8 sm:py-10 mb-0">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#d7b779] mb-3">
                IV. Master Butler
              </span>
              <h2 className="jba-display font-medium text-white text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-4">
                上級 Master Butler で問われること
              </h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] max-w-3xl">
                Masterは、自分の手つきの完成度だけを競う段階ではありません。家全体の空気を見渡し、後進へ基準を渡し、例外を運用へ織り込む設計力を問います。専門資格とあわせ、領域ごとの深まりを束ねる位置でもあります。
              </p>
            </div>
            <div className="grid lg:grid-cols-3 gap-0 border border-t-0 border-[#e2ddd2] bg-white mb-8">
              {masterAxes.map((item) => (
                <div key={item.en} className="px-6 sm:px-7 py-7 border-b lg:border-b-0 lg:border-r last:border-r-0 border-[#e2ddd2]">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">{item.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{item.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 items-center">
              <img
                src="/images/generated/jba-std-certification-05.jpg"
                alt="後進へ基準を示範する上級執事の姿"
                className="w-full h-48 object-cover border border-[#a38e65]/30"
              />
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                上級で見る示範は、長い説教ではありません。短い確認、静かな手直し、記録の見出しの付け方——日常の縮図のなかで、品位の下限が伝わるかを確認します。個人のカリスマに依存した現場は、担当が変わった瞬間に揺れます。Masterの役割は、揺れを減らす言葉と型を残すことにあります。
              </p>
            </div>
          </section>

          {/* S5. 専門資格 — 左本文＋右縦スタック */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Specialist Credentials
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              専門資格の位置づけ
            </h2>
            <div className="grid lg:grid-cols-[1fr_1fr] gap-10 items-start">
              <div className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <p>
                  当協会の資格体系は、初級Associate・中級Professional・上級Masterの三段階に、専門資格を重ねる形をとっています。専門は、縦の成長を置き換えるものではなく、特定領域における判断と手順の厚みを補う横の軸です。
                </p>
                <p>
                  領域が変わっても、観察・記録・自制・言葉・段取りという素養の芯は共通です。芯が定まっていなければ、専門知識は用語の装飾に終わります。芯が定まっていれば、新しい知識は依頼者の生活の静けさへ変換されます。
                </p>
                <p>
                  どの専門を、どの段階で深めるかは、家の要請と本人の実務に依存します。制度の見取り図は資格制度の頁を、人物側の座標は素養の頁を、あわせて参照してください。
                </p>
              </div>
              <div className="space-y-0 border border-[#e2ddd2] bg-white">
                {specialtyRoles.map((item, idx) => (
                  <div
                    key={item.label}
                    className={`px-6 py-6 ${
                      idx < specialtyRoles.length - 1 ? 'border-b border-[#e2ddd2]' : ''
                    }`}
                  >
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 flex items-center gap-3">
                      <span className="text-[#a38e65] font-['Playfair_Display',serif] text-sm tracking-[0.12em] shrink-0">
                        0{idx + 1}
                      </span>
                      <span>{item.label}</span>
                    </h3>
                    <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#4a4638] leading-[1.9] pl-9">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* S6. 実技審査 — 中央見出し＋三枚観点＋下帯 */}
          <section>
            <div className="text-center mb-10">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                VI. Practical Examination
              </span>
              <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                実技審査で見る観点——所作・接遇・段取り
              </h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mx-auto">
                実技は、知識の暗唱を動作で再演する場ではありません。限られた場面のなかで、所作・接遇・段取りが一本の流れとして品位を保っているかを見ます。部分点の足し算より、途切れない静けさと、必要な確認が残っているかを重視します。
              </p>
            </div>
            <div className="mb-8">
              <img
                src="/images/generated/jba-std-certification-06.jpg"
                alt="所作と接遇と段取りの質を見極める実技の観点"
                className="w-full h-56 sm:h-64 object-cover border border-[#a38e65]/30"
              />
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {practicalLenses.map((item) => (
                <div key={item.title} className="border-t-2 border-[#1a2942] pt-5">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{item.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#fbfaf7] border-l-2 border-[#a38e65] px-6 sm:px-8 py-6">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#333] leading-[1.95]">
                三つの観点は分離した採点表というより、互いに支え合うレンズです。所作が整っていても接遇が相手を急かしていれば場は固くなり、接遇が丁寧でも段取りが崩れていれば安心は続きません。実技では、目立たない確認——視線、間、申し送りの一文——が、奉仕の品質として現れているかを静かに見ます。
              </p>
            </div>
          </section>

          {/* S7. 継続研鑽 — 2×2habitsカード */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Continued Cultivation
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              認定後の継続研鑽
            </h2>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-4 mb-8 flex flex-wrap items-center gap-x-8 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">AFTER CERTIFICATION</span>
              <span className="font-['Noto_Serif_JP',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                認定は起点であり、完成の印ではない
              </span>
            </div>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              認定は、その時点で当協会が定める観点を満たしたことを示す通過点です。暮らしの様式も、通信の手段も、依頼者の背景も変わり続けます。認定後に研鑽を止めた瞬間から、基準は過去の記憶へ薄れていきます。
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {continuumItems.map((item) => (
                <div
                  key={item.en}
                  className="bg-white border border-[#e2ddd2] p-7"
                >
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em]">{item.en}</span>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg">{item.ja}</h3>
                  </div>
                  <div className="w-8 h-px bg-[#a38e65] mb-4"></div>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* S8. 認定の限界＋FAQ — 8本目h2 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. Limits of Certification
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              認定の限界——保証しないことを明示する
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              基準を示すことと、過大な期待を背負わせることは別です。当協会の資格は民間認定であり国家資格ではありません。認定が意味する範囲を超えて、就業や技能の永久保証であるかのように語ることを、協会自身が戒めます。
            </p>
            <div className="grid lg:grid-cols-3 gap-6 mb-12">
              {limitPoints.map((item) => (
                <div key={item.title} className="bg-[#fbfaf7] border border-[#e2ddd2] p-7">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 pb-3 border-b border-[#e2ddd2]">{item.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10">
              限界を明示することは、認定の価値を下げる行為ではありません。むしろ、依頼者・学び手・協会の三者が同じ現実認識に立つための礼儀です。保証しないことを誠実に述べたうえで、なお残る——その時点の観点を満たしたという事実——だけを、静かに差し出す。それが民間認定の品位だと考えます。
            </p>

            <div className="border-t-2 border-[#1a2942]">
              <p className="font-['Playfair_Display',serif] uppercase tracking-[0.18em] text-[12px] text-[#8f7a56] pt-6 mb-4">
                Questions
              </p>
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* S9. 関連導線 — 9th section（h2なし：8 h2を厳守） */}
          <section>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#d7b779] mb-4">
                Related Paths
              </span>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                認定基準は、素養の座標・資格の段階・なり方の順序と読み合わせて初めて、日常の手つきへ落ちます。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、民間認定としての境界を守りながら、いまの暮らしにふさわしい評価の言葉を開き続けます。代表理事・石井裕一（三代目頭取）のもと、基準は完成した法典ではなく、現場の静けさを守るための共有言語です。
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/standards/aptitude" className={BTN08_ON_NAVY}>
                  執事に必要な素養
                </a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>
                  資格制度を見る
                </a>
                <a href="/butler/become" className={BTN08_ON_NAVY}>
                  執事になるには
                </a>
              </div>
              <div className="mt-6">
                <a href="/standards" className={BTN08_ON_LIGHT}>
                  基準・指針の一覧
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
